// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';

const Login = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // MFA second-step state. When backend returns mfa_required, we stash the
  // challenge token here and switch the form into "enter code" mode.
  const [mfa, setMfa] = useState({ token: null, expiresAt: null });
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const { login, verifyMfaLogin } = useAuth();
  const [searchParams] = useSearchParams();

  // Debug: Log return URL sources on mount
  useEffect(() => {
    const queryUrl = searchParams.get('returnUrl');
    const sessionUrl = sessionStorage.getItem('returnUrl');
    console.log('Login page mounted:', { queryUrl, sessionUrl });
  }, [searchParams]);

  // Get return URL - query param first, then sessionStorage, then default
  const getReturnUrl = () => {
    // Primary: Query parameter (most reliable)
    const queryReturnUrl = searchParams.get('returnUrl');
    if (queryReturnUrl) {
      console.log('Using returnUrl from query param:', queryReturnUrl);
      return decodeURIComponent(queryReturnUrl);
    }
    
    // Backup: sessionStorage
    const sessionReturnUrl = sessionStorage.getItem('returnUrl');
    if (sessionReturnUrl) {
      console.log('Using returnUrl from sessionStorage:', sessionReturnUrl);
      return sessionReturnUrl;
    }
    
    // Default: Network Topology page
    console.log('No returnUrl found, using default: /topology');
    return '/topology';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const navigateAfterLogin = () => {
    const destination = getReturnUrl();
    sessionStorage.removeItem('returnUrl');
    console.log('Login successful, redirecting to:', destination);
    window.location.replace(destination);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(credentials.username, credentials.password);

      if (result.success) {
        navigateAfterLogin();
        return;
      }
      if (result.mfaRequired) {
        // Switch to step 2 — challenge token already proves password.
        const expiresAt = Date.now() + (result.expiresInSeconds || 300) * 1000;
        setMfa({ token: result.mfaToken, expiresAt });
        setSecondsLeft(result.expiresInSeconds || 300);
        return;
      }
      setError(result.error || 'Invalid credentials');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const args = useRecovery
        ? { recoveryCode }
        : { code: mfaCode };
      const result = await verifyMfaLogin(mfa.token, args);
      if (result.success) {
        navigateAfterLogin();
        return;
      }
      setError(result.error || 'Invalid verification code');
      // Clear whichever input was just rejected so the user retypes
      // — recovery codes especially shouldn't sit in the field after
      // a failed attempt (a successful attempt would have burned it,
      // a failed one means typo and they should try the next code).
      if (useRecovery) setRecoveryCode(''); else setMfaCode('');
    } catch (err) {
      console.error('MFA verify error:', err);
      setError(err.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Countdown for the challenge token. On expiry, drop back to step 1.
  useEffect(() => {
    if (!mfa.token || !mfa.expiresAt) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((mfa.expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setMfa({ token: null, expiresAt: null });
        setMfaCode('');
        setError('Verification window expired. Please sign in again.');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [mfa.token, mfa.expiresAt]);

  const handleCancelMfa = () => {
    setMfa({ token: null, expiresAt: null });
    setMfaCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setError('');
  };

  const toggleRecoveryMode = () => {
    setUseRecovery((prev) => !prev);
    setError('');
    setMfaCode('');
    setRecoveryCode('');
  };

  const inMfaStep = mfa.token !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">AI Sentry Management</h2>
          {inMfaStep ? (
            <>
              <h3 className="text-xl font-medium text-gray-700 mb-2">Two-factor verification</h3>
              <p className="text-gray-500 mb-2">Enter the 6-digit code from your authenticator app.</p>
              <p className="text-xs text-gray-400 mb-6">
                Code expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
              </p>
            </>
          ) : (
            <>
              <h3 className="text-xl font-medium text-gray-700 mb-2">Sign in to your account</h3>
              <p className="text-gray-500 mb-6">Enter your credentials to access the dashboard</p>
            </>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {inMfaStep && (
          <form onSubmit={handleMfaSubmit} className="space-y-6">
            {!useRecovery && (
              <div>
                <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700">
                  Verification code
                </label>
                <input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1 appearance-none block w-full px-3 py-3 text-center text-2xl tracking-[0.4em] font-mono border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="000000"
                />
              </div>
            )}
            {useRecovery && (
              <div>
                <label htmlFor="recovery-code" className="block text-sm font-medium text-gray-700">
                  Recovery code
                </label>
                <input
                  id="recovery-code"
                  name="recovery-code"
                  type="text"
                  autoComplete="off"
                  required
                  autoFocus
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  className="mt-1 appearance-none block w-full px-3 py-3 text-center text-lg tracking-widest font-mono border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="xxxxx-xxxxx"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Each recovery code can only be used once.
                </p>
              </div>
            )}
            <div className="text-center">
              <button
                type="button"
                onClick={toggleRecoveryMode}
                disabled={isLoading}
                className="text-sm text-blue-600 hover:text-blue-500 underline-offset-2 hover:underline"
              >
                {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelMfa}
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={
                  isLoading ||
                  (useRecovery ? recoveryCode.replace(/[\s-]/g, '').length < 10 : mfaCode.length !== 6)
                }
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </form>
        )}

        {!inMfaStep && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              Username
            </label>
            <div className="mt-1">
              <input
                id="username"
                name="username"
                type="text"
                required
                value={credentials.username}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your username"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                required
                value={credentials.password}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember_me"
                name="remember_me"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>
        )}

        {!inMfaStep && (
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div>
              <a href="#" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                </svg>
              </a>
            </div>

            <div>
              <a href="#" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Login;
